import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, Refresh, InfoOutlined, Close, ListAlt } from '@mui/icons-material';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { dailyAccountAPI, lookupAPI } from '../services/api';
import { Store, CashPositionReport, BankDebitRow, ExpenseBreakdownRow } from '../types';

// "building_rent" -> "Building Rent"
const prettyCategory = (c: string) =>
  c === '(untagged)' ? 'Untagged' : c.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());

const inr = (n: number | null | undefined) =>
  n == null ? '—' : `₹${Math.round(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Purchases / Expenses / Uncategorized / Sales are fixed categorical buckets — colors
// assigned by identity, not by whether the number is "good" or "bad" (that's the Net
// Change card's job, which uses red/green separately). Validated for CVD-safe adjacent
// separation and chroma floor via the dataviz skill's validate_palette.js.
const COLOR_PURCHASES = '#eb6834';
const COLOR_EXPENSES = '#2a78d6';
const COLOR_UNCATEGORIZED = '#eda100';
const COLOR_SALES = '#1baf7a';
const COLOR_POSITION = '#2c3e50';

function netChangeChip(value: number | null) {
  if (value == null) return <Chip size="small" label="—" />;
  if (value > 0) return <Chip size="small" icon={<TrendingUp />} color="success" label={inr(value)} />;
  if (value < 0) return <Chip size="small" icon={<TrendingDown />} color="error" label={inr(value)} />;
  return <Chip size="small" icon={<TrendingFlat />} label={inr(value)} />;
}

const FinancialAnalysisReport: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().subtract(1, 'day'));
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs().subtract(1, 'day'));
  const [report, setReport] = useState<CashPositionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [bankDebitsOpen, setBankDebitsOpen] = useState(false);
  const [bankDebits, setBankDebits] = useState<BankDebitRow[]>([]);
  const [bankDebitsLoading, setBankDebitsLoading] = useState(false);

  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expenseRows, setExpenseRows] = useState<ExpenseBreakdownRow[]>([]);
  const [expenseLoading, setExpenseLoading] = useState(false);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => {});
  }, []);

  const loadReport = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setError('');
    try {
      const data = await dailyAccountAPI.getCashPositionReport({
        store_id: selectedStore,
        date_from: dateFrom.format('YYYY-MM-DD'),
        date_to: dateTo.format('YYYY-MM-DD'),
      });
      setReport(data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load cash position report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedStore, dateFrom, dateTo]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const summary = report && report.by_store.length > 0 ? report.summary : null;
  const rangeDays = dateFrom && dateTo ? dateTo.diff(dateFrom, 'day') + 1 : 0;
  const singleStoreId = selectedStore !== 'all' ? Number(selectedStore) : null;
  const dailyDetail = singleStoreId && report && report.trend.length === 1 ? report.trend[0] : null;

  const openBankDebits = async () => {
    if (!singleStoreId || !dateFrom || !dateTo) return;
    setBankDebitsOpen(true);
    setBankDebitsLoading(true);
    try {
      const res = await dailyAccountAPI.getBankDebits({
        store_id: singleStoreId,
        date_from: dateFrom.format('YYYY-MM-DD'),
        date_to: dateTo.format('YYYY-MM-DD'),
      });
      setBankDebits(res.rows);
    } catch {
      setBankDebits([]);
    } finally {
      setBankDebitsLoading(false);
    }
  };

  // Works for a single store or All Stores (category is comparable across stores).
  const openExpenseBreakdown = async () => {
    if (!dateFrom || !dateTo) return;
    setExpenseOpen(true);
    setExpenseLoading(true);
    try {
      const res = await dailyAccountAPI.getExpenseBreakdown({
        store_id: selectedStore,
        date_from: dateFrom.format('YYYY-MM-DD'),
        date_to: dateTo.format('YYYY-MM-DD'),
      });
      setExpenseRows(res.items);
    } catch {
      setExpenseRows([]);
    } finally {
      setExpenseLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Financial Analysis Report</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cash + Bank position by store — opening vs closing, and whether the change is from purchases or expenses.
        Purchases and Sales are sourced from the Purchase Payments and Sales screens (evitalrx), not from Daily Account tags.
      </Typography>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Store</InputLabel>
          <Select value={selectedStore} label="Store" onChange={e => setSelectedStore(e.target.value)}>
            <MenuItem value="all">All Stores</MenuItem>
            {stores.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.store_name}</MenuItem>)}
          </Select>
        </FormControl>
        <DatePicker label="From" value={dateFrom} onChange={setDateFrom}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
        <DatePicker label="To" value={dateTo} onChange={setDateTo}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
        <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadReport} disabled={loading}>
          Refresh
        </Button>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !summary ? (
        <Alert severity="info">No Daily Account, Purchase, or Sales data found for this period.</Alert>
      ) : (
        <>
          {/* KPI cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Opening Position</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.opening_total)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Closing Position</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.closing_total)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Net Change</Typography>
                <Box sx={{ mt: 0.5 }}>{netChangeChip(summary.net_change)}</Box>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Opening Cash</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_opening_cash)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Closing Cash</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_closing_cash)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Opening Bank</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_opening_bank_balance)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Closing Bank</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_closing_bank_balance)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: COLOR_SALES, display: 'inline-block' }} />
                  Total Sales
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_sales)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: COLOR_PURCHASES, display: 'inline-block' }} />
                  Total Purchases
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_purchases)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">Current Stock Value (By LP)</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_landing_price_stock_value)}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: COLOR_EXPENSES, display: 'inline-block' }} />
                  Total Expenses
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_expenses)}</Typography>
                {summary.total_expenses > 0 && (
                  <Button size="small" startIcon={<ListAlt />} onClick={openExpenseBreakdown} sx={{ mt: 0.5, ml: -0.5 }}>
                    View breakdown
                  </Button>
                )}
              </CardContent>
            </Card>
            {summary.total_uncategorized_bank_debits > 100 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: COLOR_UNCATEGORIZED, display: 'inline-block' }} />
                    Uncategorized Bank Spend
                    <MuiTooltip title="Money left the bank per the bank statement, but staff didn't tag it as an Expense category in Daily Account. This likely includes distributor payments already counted in Total Purchases above (paid via bank/UPI/NEFT), plus any other untracked bank spend — it isn't necessarily on top of Purchases.">
                      <InfoOutlined sx={{ fontSize: 14 }} />
                    </MuiTooltip>
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{inr(summary.total_uncategorized_bank_debits)}</Typography>
                  {singleStoreId && (
                    <Button size="small" startIcon={<ListAlt />} onClick={openBankDebits} sx={{ mt: 0.5, ml: -0.5 }}>
                      View breakdown
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
            {Math.abs(summary.total_unexplained) > 100 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Unexplained
                    <MuiTooltip title="Whatever is left after purchases, expenses, and uncategorized bank spend are subtracted from the day's net change — usually small rounding between the physical cash count and the logged ledger entries.">
                      <InfoOutlined sx={{ fontSize: 14 }} />
                    </MuiTooltip>
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>{inr(summary.total_unexplained)}</Typography>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Daily detail: raw cash/bank fields for a single store on a single day */}
          {dailyDetail && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Day Detail — {dayjs(dailyDetail.date).format('DD-MMM-YYYY')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Opening Cash</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{inr(dailyDetail.opening_cash)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Closing Cash</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{inr(dailyDetail.closing_cash)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Opening Bank</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{inr(dailyDetail.opening_bank_balance)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Closing Bank</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{inr(dailyDetail.closing_bank_balance)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Bank Credits</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>{inr(dailyDetail.bank_credits)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Bank Debits</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>{inr(dailyDetail.bank_debits)}</Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Trend chart */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Cash + Bank Position Over Time {selectedStore === 'all' ? '(Company-wide)' : ''}
            </Typography>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={report?.company_trend || []} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => dayjs(d).format('DD-MMM')} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  labelFormatter={d => dayjs(d as string).format('DD-MMM-YYYY')}
                  formatter={(value: number, name: string) => [inr(value), name]}
                />
                <Legend />
                <Bar dataKey="sales" name="Sales" fill={COLOR_SALES} barSize={12} />
                <Bar dataKey="purchases" name="Purchases" fill={COLOR_PURCHASES} barSize={12} />
                <Bar dataKey="expenses" name="Expenses" fill={COLOR_EXPENSES} barSize={12} />
                <Bar dataKey="uncategorized_bank_debits" name="Uncategorized Bank Spend" fill={COLOR_UNCATEGORIZED} barSize={12} />
                <Line type="monotone" dataKey="closing_total" name="Closing Position" stroke={COLOR_POSITION} strokeWidth={2} dot={false} connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>

          {/* Per-store breakdown */}
          {selectedStore === 'all' && report && report.by_store.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Per-Store Breakdown (worst cash burn first)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell><strong>Store</strong></TableCell>
                      <TableCell align="right"><strong>Opening</strong></TableCell>
                      <TableCell align="right"><strong>Closing</strong></TableCell>
                      <TableCell align="right"><strong>Opening Cash</strong></TableCell>
                      <TableCell align="right"><strong>Closing Cash</strong></TableCell>
                      <TableCell align="right"><strong>Opening Bank</strong></TableCell>
                      <TableCell align="right"><strong>Closing Bank</strong></TableCell>
                      <TableCell align="right"><strong>Stock Value (LP)</strong></TableCell>
                      <TableCell align="center"><strong>Net Change</strong></TableCell>
                      <TableCell align="right"><strong>Sales</strong></TableCell>
                      <TableCell align="right"><strong>Purchases</strong></TableCell>
                      <TableCell align="right"><strong>Expenses</strong></TableCell>
                      <TableCell align="right"><strong>Uncategorized Bank</strong></TableCell>
                      <TableCell align="center"><strong>Days Reported</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.by_store.map(s => (
                      <TableRow key={s.store_id} hover>
                        <TableCell>{s.store_name || `Store ${s.store_id}`}</TableCell>
                        <TableCell align="right">{inr(s.opening_total)}</TableCell>
                        <TableCell align="right">{inr(s.closing_total)}</TableCell>
                        <TableCell align="right">{inr(s.opening_cash)}</TableCell>
                        <TableCell align="right">{inr(s.closing_cash)}</TableCell>
                        <TableCell align="right">{inr(s.opening_bank_balance)}</TableCell>
                        <TableCell align="right">{inr(s.closing_bank_balance)}</TableCell>
                        <TableCell align="right">{inr(s.landing_price_stock_value)}</TableCell>
                        <TableCell align="center">{netChangeChip(s.net_change)}</TableCell>
                        <TableCell align="right" sx={{ color: COLOR_SALES, fontWeight: 600 }}>{inr(s.sales)}</TableCell>
                        <TableCell align="right" sx={{ color: COLOR_PURCHASES, fontWeight: 600 }}>{inr(s.purchases)}</TableCell>
                        <TableCell align="right" sx={{ color: COLOR_EXPENSES, fontWeight: 600 }}>{inr(s.expenses)}</TableCell>
                        <TableCell align="right" sx={{ color: COLOR_UNCATEGORIZED, fontWeight: 600 }}>{inr(s.uncategorized_bank_debits)}</TableCell>
                        <TableCell align="center">
                          {s.days_reported === 0 ? (
                            <MuiTooltip title="No Daily Account entries for this store in range — position is unknown, but Purchases/Sales are still shown">
                              <Chip size="small" color="warning" label="No data" />
                            </MuiTooltip>
                          ) : s.days_reported < rangeDays ? (
                            <MuiTooltip title={`Only ${s.days_reported} of ${rangeDays} days in range have daily account entries — numbers may be incomplete`}>
                              <Chip size="small" color="warning" label={`${s.days_reported}/${rangeDays}`} />
                            </MuiTooltip>
                          ) : (
                            <Chip size="small" label={`${s.days_reported}/${rangeDays}`} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      <Dialog open={bankDebitsOpen} onClose={() => setBankDebitsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Uncategorized Bank Spend — Line Items
          <IconButton size="small" onClick={() => setBankDebitsOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {bankDebitsLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : bankDebits.length === 0 ? (
            <Alert severity="info">No bank debit transactions found for this store/date range.</Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                These are the raw bank statement debits for this store/range. Some may already be counted in Total Purchases
                above (e.g. UPI/NEFT payments to distributors) — this list isn't purely "extra" spend on top of Purchases.
              </Alert>
              {bankDebits.some(r => r.possible_duplicate) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {bankDebits.filter(r => r.possible_duplicate).length} transactions (marked ⚠) have the same amount as an
                  adjacent entry in the parsed bank statement — this can be two real payments minutes apart, or the PDF
                  parser splitting one wrapped transaction into two rows. Not removed automatically; verify against the
                  actual statement before treating both as separate spend.
                </Alert>
              )}
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Particulars</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bankDebits.map(r => (
                      <TableRow key={r.id} hover sx={r.possible_duplicate ? { bgcolor: '#fff8e1' } : undefined}>
                        <TableCell sx={{ width: 28 }}>
                          {r.possible_duplicate && (
                            <MuiTooltip title="Same amount as an adjacent bank statement entry — possible duplicate, verify against the statement">
                              <span>⚠</span>
                            </MuiTooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(r.date).format('DD-MMM-YY')}</TableCell>
                        <TableCell sx={{ fontSize: 12, wordBreak: 'break-word', maxWidth: 400 }}>{r.particulars}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#fff3e0' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 700 }}>TOTAL ({bankDebits.length} transactions)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(bankDebits.reduce((s, r) => s + r.amount, 0))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={expenseOpen} onClose={() => setExpenseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Total Expenses — Breakdown by Category
          <IconButton size="small" onClick={() => setExpenseOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {expenseLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : expenseRows.length === 0 ? (
            <Alert severity="info">No tagged expenses found for this store/date range.</Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Operating expenses only — cash-drawer spend plus tagged bank expenses. Distributor purchases are excluded
                (they come from the Purchases figure), as are cash↔bank transfers.
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell><strong>Paid via</strong></TableCell>
                      <TableCell align="right"><strong>Entries</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expenseRows.map((r, i) => (
                      <TableRow key={`${r.source}-${r.category}-${i}`} hover>
                        <TableCell>{prettyCategory(r.category)}</TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={r.source === 'bank' ? 'Bank' : 'Cash'}
                            color={r.source === 'bank' ? 'primary' : 'default'} />
                        </TableCell>
                        <TableCell align="right">{r.count}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(r.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#eaf1fb' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(expenseRows.reduce((s, r) => s + r.amount, 0))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default FinancialAnalysisReport;
